import { BaseRepository } from './BaseRepository';

/**
 * Call Center System Config entity type
 */
export interface SystemConfig {
  id: string;
  company_id: string;
  auto_call_distribution: boolean;
  ring_strategy: 'round_robin' | 'least_recent' | 'skill_based' | 'priority';
  max_ring_time: number;
  voicemail_enabled: boolean;
  callback_enabled: boolean;
  quality_monitoring: boolean;
  ai_assistance: boolean;
  real_time_transcription: boolean;
  sentiment_analysis: boolean;
  target_answer_rate: number;
  target_quality_score: number;
  max_avg_wait_time: number;
  created_at: string;
  updated_at: string;
}

/**
 * Repository for managing call center system configuration
 * Provides CRUD operations for system settings (one per company)
 */
export class SystemConfigRepository extends BaseRepository<SystemConfig> {
  constructor() {
    super({ tableName: 'call_center_system_config', primaryKey: 'id' });
  }

  /**
   * Find system config for a specific company
   * Note: Each company should have only one system config
   */
  async findByCompany(companyId: string): Promise<SystemConfig | null> {
    const configs = await this.findAll({
      filters: { company_id: companyId },
      limit: 1
    });

    return configs.length > 0 ? configs[0] : null;
  }

  /**
   * Create or update system config for a company
   * Ensures only one config exists per company
   */
  async upsert(companyId: string, data: Partial<SystemConfig>): Promise<SystemConfig> {
    const existing = await this.findByCompany(companyId);

    if (existing) {
      return this.update(existing.id, data);
    } else {
      return this.create({
        ...data,
        company_id: companyId
      });
    }
  }

  /**
   * Toggle auto call distribution
   */
  async toggleACD(companyId: string): Promise<SystemConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('System config not found for company');
    }

    return this.update(config.id, { 
      auto_call_distribution: !config.auto_call_distribution 
    });
  }

  /**
   * Update ring strategy
   */
  async updateRingStrategy(
    companyId: string,
    strategy: SystemConfig['ring_strategy']
  ): Promise<SystemConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('System config not found for company');
    }

    return this.update(config.id, { ring_strategy: strategy });
  }

  /**
   * Update feature toggles
   */
  async updateFeatures(
    companyId: string,
    features: Partial<Pick<SystemConfig, 
      'voicemail_enabled' | 
      'callback_enabled' | 
      'quality_monitoring' | 
      'ai_assistance' | 
      'real_time_transcription' | 
      'sentiment_analysis'
    >>
  ): Promise<SystemConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('System config not found for company');
    }

    return this.update(config.id, features);
  }

  /**
   * Update performance targets
   */
  async updateTargets(
    companyId: string,
    targets: Partial<Pick<SystemConfig, 
      'target_answer_rate' | 
      'target_quality_score' | 
      'max_avg_wait_time'
    >>
  ): Promise<SystemConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('System config not found for company');
    }

    return this.update(config.id, targets);
  }

  /**
   * Update max ring time
   */
  async updateMaxRingTime(
    companyId: string,
    seconds: number
  ): Promise<SystemConfig> {
    const config = await this.findByCompany(companyId);
    if (!config) {
      throw new Error('System config not found for company');
    }

    if (seconds < 10 || seconds > 120) {
      throw new Error('Max ring time must be between 10 and 120 seconds');
    }

    return this.update(config.id, { max_ring_time: seconds });
  }
}

// Export singleton instance
export const systemConfigRepository = new SystemConfigRepository();
